/**
 * The two steps before a post exists, as the page itself: the moment box (the words, the shape,
 * the kind, a proven post to follow, the host's own photos), then the photos we'd use with what
 * the shape needs and Make it. AI is always the road; by hand is only the quiet link, and it is
 * never remembered. Make it spends the credits and hands everything to the composer. The words
 * of a half-finished wizard are kept on this device.
 */
import type { MediaDto } from '@str/api/types';
import {
  BRIEF_MAX,
  cardCountFor,
  creditSpend,
  type PostFormat,
  type PostTemplate,
} from '@str/shared';
import { ApiError } from '@/lib/api';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Fidelity } from '@/lib/moment';
import { mediaStyle } from '@/lib/scenes';
import { Moment } from './Moment';
import { ProvenPicker, type ProvenPost } from './ProvenPicker';
import {
  lineTooLong,
  SHAPES,
  canGoOn,
  followingLine,
  loadWizard,
  mediaModeFor,
  pictureMadeBy,
  pickLimit,
  pickSummary,
  saveWizard,
  togglePick,
  wizardFormat,
  type Following,
  type Kind,
  type Path,
  type Shape,
  type ShapeInput,
  type WizardStep,
} from '@/lib/wizard';

export type WizardMake = {
  path: Path;
  kind: Kind;
  format: PostFormat;
  picks: MediaDto[];
  shape: Shape;
  line: string;
  input: ShapeInput;
  /** The form of the Inspiration post being followed, and whose it was; null when none. */
  template: PostTemplate | null;
  inspirationPostId: string | null;
  following: Following | null;
};

export type WizardReview = { id: string; quote: string; author: string; reviewedAt: string };
/** Everything the first screen knows when a reel is built: the storyboard starts from this. */
export type ReelStart = {
  line: string;
  seconds: 15 | 30;
  fidelity: Fidelity;
  template: PostTemplate | null;
  following: Following | null;
};
export type Found = { id: string; why: string };

const COUNT = ['none', 'this one', 'these two', 'these three'];
const MOMENT_HINT = 'e.g. Friends in the hot tub at sundown, drinks in hand';

export function Wizard({
  propertyId,
  photos,
  videos,
  reviews,
  bookingUrl,
  busy,
  adding = false,
  credits,
  justAdded,
  blocked = null,
  beatMs = 1500,
  onFind,
  onUpload,
  onDrop,
  onMake,
  onReel,
  startOwn = false,
  railSlot = null,
  onFindProven,
  failure = null,
}: {
  propertyId: string;
  photos: MediaDto[];
  videos: MediaDto[];
  reviews: WizardReview[];
  bookingUrl: string | null;
  busy: boolean;
  /** Files from this device are being added: the only busy that says "Adding…". */
  adding?: boolean;
  /** The wallet: what is there, when the monthly grant resets (null on a trial), and whether this is the trial pack. */
  credits: { balance: number; resetsAt: string | null; trial?: boolean } | null;
  /** Files the host just added from this device: chosen at once, by hand, and step 1 skips ahead. */
  justAdded: MediaDto[] | null;
  /** Why Make it is held (the property is off the plan): the steps stay open to read. */
  blocked?: string | null;
  /** How long "Looking at your photos…" is on screen at least, so the choosing reads as a beat. */
  beatMs?: number;
  /** The photos that fit the moment, best first, with why. */
  /** Finds the photos for a moment; `terms` are the followed post's focus words, when any. */
  onFind: (kind: Kind, line: string, terms: string[]) => Promise<Found[]>;
  onUpload: () => void;
  onDrop: (files: FileList) => void;
  onMake: (input: WizardMake) => void;
  /** A reel is built from the first screen: the storyboard starts and the reel page takes over. */
  onReel?: (start: ReelStart) => void;
  /** Open on the by-hand photos step (a reel from a video the host filmed). */
  startOwn?: boolean;
  /** Where the step rail goes once there are steps: the page header, when it offers a slot. */
  railSlot?: HTMLElement | null;
  /** Proven posts from Inspiration to follow; the box offers none without it. */
  onFindProven?: () => Promise<ProvenPost[]>;
  /** A first make that did not come out: the photos step opens again, everything kept, with this line. */
  failure?: string | null;
}) {
  const [kept] = useState(() => loadWizard(propertyId));
  const resume = !!failure && !!kept?.line.trim();
  const [step, setStep] = useState<WizardStep>(startOwn || resume ? 2 : 1);
  // AI unless the host takes the by-hand link this visit: that choice is never remembered.
  const [path, setPath] = useState<Path>(startOwn ? 'own' : 'ai');
  const [proven, setProven] = useState(false);
  const [kind, setKind] = useState<Kind>(kept?.kind ?? 'Post');
  const [line, setLine] = useState(kept?.line ?? '');
  const [seconds, setSeconds] = useState<15 | 30>(kept?.seconds ?? 15);
  const [fidelity, setFidelity] = useState<Fidelity>(kept?.fidelity ?? 'original');
  const [picks, setPicks] = useState<string[]>(kept?.picks ?? []);
  const [touched, setTouched] = useState(false);
  const [shapeId, setShapeId] = useState<string>(kept?.shapeId ?? 'idea');
  const [input, setInput] = useState<ShapeInput>(kept?.input ?? {});
  const [template, setTemplate] = useState<PostTemplate | null>(kept?.template ?? null);
  const [following, setFollowing] = useState<Following | null>(kept?.following ?? null);
  // 'failed' is a search that broke off (worth trying again); 'invalid' is an ask the API will
  // never take as it is (the idea is too long), so the way out is to edit the idea.
  const [finding, setFinding] = useState<'idle' | 'looking' | 'done' | 'failed' | 'invalid'>(
    resume ? 'done' : 'idle',
  );
  const [focusLine, setFocusLine] = useState(0);
  useEffect(() => {
    if (!focusLine) return;
    const el = document.getElementById('mo-line') as HTMLTextAreaElement | null;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [focusLine]);
  const [found, setFound] = useState<Found[]>([]);
  const [changing, setChanging] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const findSeq = useRef(0);
  // State that follows props is settled during render, the React way, not in an effect.
  const [seenAdded, setSeenAdded] = useState<MediaDto[] | null>(null);

  useEffect(() => {
    saveWizard(propertyId, {
      kind,
      path,
      line,
      picks,
      shapeId,
      input,
      template,
      following,
      seconds,
      fidelity,
    });
  }, [propertyId, kind, path, line, picks, shapeId, input, template, following, seconds, fidelity]);

  const all = new Map<string, MediaDto>();
  for (const m of [...photos, ...videos, ...(justAdded ?? [])]) all.set(m.id, m);
  const usablePhotos = [...all.values()].filter((m) => m.kind === 'photo' && !!m.url);
  const usableVideos = [...all.values()].filter((m) => m.kind === 'video' && !!m.url);
  const shape = SHAPES.find((s) => s.id === shapeId) ?? SHAPES[0]!;
  const limit = pickLimit(kind);

  // Files the host just added are theirs, by hand: chosen at once; from step 1 that skips ahead.
  if (justAdded !== seenAdded) {
    setSeenAdded(justAdded);
    if (justAdded?.length) {
      const video = justAdded.find((m) => m.kind === 'video');
      setTouched(true);
      // A video is a reel of that video, by hand; a photo rides along and the AI still makes it.
      if (video) setPath('own');
      if (video) {
        setKind('Reel');
        setPicks([video.id]);
      } else {
        const ids = justAdded.filter((m) => m.kind === 'photo').map((m) => m.id);
        setKind('Post');
        setPicks(
          [...picks.filter((id) => all.get(id)?.kind === 'photo'), ...ids]
            .filter((id, i, arr) => arr.indexOf(id) === i)
            .slice(0, limit),
        );
      }
      if (video && step === 1) setStep(2);
    }
  }
  const chosen = picks.map((id) => all.get(id)).filter((m): m is MediaDto => !!m);
  const chosenPhotos = chosen.filter((m) => m.kind === 'photo');
  const ctx = { path, line, input };
  const goOn = canGoOn(step, kind, chosen, shape, ctx);

  /** The AI is choosing: ask for the photos that fit, and hold the beat so it reads as a look. */
  const find = (k: Kind, text: string) => {
    const seq = ++findSeq.current;
    setFinding('looking');
    setChanging(false);
    setTouched(false);
    const started = Date.now();
    void Promise.resolve()
      .then(() => onFind(k, text, template?.subjectTerms ?? []))
      .then(async (refs) => {
        const wait = beatMs - (Date.now() - started);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        if (seq !== findSeq.current) return;
        // Kept as ids: they show as the library loads, so a search that beats it loses nothing.
        const ids = refs.map((r) => r.id).filter((id, i, arr) => arr.indexOf(id) === i);
        setFound(refs);
        setPicks(ids.slice(0, 3));
        setFinding('done');
      })
      .catch((e: unknown) => {
        if (seq !== findSeq.current) return;
        setFinding(e instanceof ApiError && e.code === 'validation' ? 'invalid' : 'failed');
      });
  };
  /** Back to the idea, cursor at its end, nothing lost: the way out of an ask the API refuses. */
  const editIdea = () => {
    findSeq.current++;
    setFinding('idle');
    setStep(1);
    setFocusLine((n) => n + 1);
  };
  const tooLong = lineTooLong(line);
  const goPhotos = () => {
    // Photos the host added in the box are the ones: nothing searches over them.
    if (path === 'ai' && touched && picks.length) {
      findSeq.current++;
      setFound([]);
      setFinding('done');
    } else if (path === 'ai') find(kind, line);
    setStep(2);
  };
  /** The photos on the screen, as they are: the by-hand road, keeping what is chosen. */
  const useAsTheyAre = () => {
    findSeq.current++;
    setTouched(true);
    setPath('own');
  };
  const startByHand = () => {
    findSeq.current++;
    setPath('own');
    if (!touched) setPicks([]);
    setStep(2);
  };
  const backToAi = () => {
    setPath('ai');
    if (!line.trim()) {
      setStep(1);
    } else if (touched && chosen.length && chosenPhotos.length === chosen.length) {
      // The photos the host narrowed down to are the ones: nothing searches over them.
      findSeq.current++;
      setFinding('done');
    } else {
      find(kind, line);
    }
  };
  const chooseKind = (k: Kind) => {
    if (k === kind) return;
    setKind(k);
    setTouched(false);
    setPicks([]);
  };
  /** The one button on the first screen: a post goes to its photos, a reel builds its storyboard. */
  const go = () => {
    if (kind === 'Reel' && path === 'ai' && onReel) {
      onReel({ line, seconds, fidelity, template, following });
      return;
    }
    goPhotos();
  };
  const tap = (m: MediaDto) => {
    setTouched(true);
    setPicks((cur) => {
      if (kind === 'Reel' && path === 'own') return cur.includes(m.id) ? [] : [m.id];
      return togglePick(
        cur.filter((id) => all.get(id)?.kind === 'photo'),
        m.id,
        limit,
      );
    });
  };
  /** Not this one: the post is let go of, and the shape is the default again. */
  const letGo = () => {
    setTemplate(null);
    setFollowing(null);
    setShapeId('idea');
  };
  const chooseShape = (s: Shape) => {
    setShapeId(s.id);
    if (s.needs === 'dates' && !input.dates)
      setInput((cur) => ({ ...cur, dates: { dates: '', price: '', link: bookingUrl ?? '' } }));
  };
  const make = () => {
    // Kept until the post lands: a make that does not come out comes back here with it all.
    const sent: ShapeInput = {};
    if (shape.needs === 'quote' && input.quote) sent.quote = input.quote;
    if (shape.needs === 'dates' && input.dates) sent.dates = input.dates;
    onMake({
      path,
      kind,
      format,
      picks: chosen,
      shape,
      line,
      input: sent,
      template,
      inspirationPostId: following?.inspirationPostId ?? null,
      following,
    });
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files.length) onDrop(e.dataTransfer.files);
  };

  const rail: { n: WizardStep; label: string }[] = [
    { n: 1, label: 'Moment' },
    { n: 2, label: 'Photos' },
  ];
  /** A proven post picked in the box: its form is followed, the host's words and photos make it. */
  const follow = (p: ProvenPost) => {
    setProven(false);
    if (!p.template) return;
    setTemplate(p.template);
    setFollowing({ handle: p.handle, imageUrl: p.imageUrl, inspirationPostId: p.id });
    setShapeId(p.template.shapeId);
  };
  const ownReel = path === 'own' && kind === 'Reel';
  /**
   * What Make it makes and spends: a Reel films a clip; a Post is a new picture of the place from the
   * photos, a card each for a carousel (the chosen photos become its pins, so the server counts
   * the same cards). By hand the host's original media and words are prepared for free.
   */
  const format = wizardFormat(kind, chosen, path, shape);
  const cards = path === 'own' ? 1 : cardCountFor(format, chosenPhotos.length);
  const mode =
    path === 'ai' && kind === 'Post' && step > 1 && finding === 'done' && !chosenPhotos.length
      ? 'imagine'
      : mediaModeFor(path, shapeId);
  const spend =
    path === 'own' ? 0 : creditSpend({ format, mediaMode: mode, ownVideo: ownReel, cards });
  const plural = (n: number) => (n === 1 ? 'credit' : 'credits');
  const short = !!credits && spend > credits.balance;
  const creditsLeft = credits
    ? short
      ? `This needs ${spend} ${plural(spend)} and you have ${credits.balance}.`
      : credits.trial
        ? `Trial · ${credits.balance} ${plural(credits.balance)}.`
        : `${credits.balance} ${plural(credits.balance)}${credits.resetsAt ? ` · resets ${resetDay(credits.resetsAt)}` : ''}.`
    : '';
  const n = chosenPhotos.length;
  const useHeading =
    finding === 'looking'
      ? 'Looking at your photos…'
      : finding === 'invalid'
        ? 'Your idea needs a trim'
        : finding === 'failed'
          ? 'Photos not chosen yet'
          : n === 0
            ? 'None chosen · we imagine it from your words'
            : `${touched ? 'You’d' : 'We’d'} use ${COUNT[n] ?? `these ${n}`}`;
  const whyOf = (id: string) => found.find((f) => f.id === id)?.why ?? 'Your pick';
  const makeLabel =
    spend === 0
      ? 'Make it · free'
      : `Make it · ${spend} ${plural(spend)}${cards > 1 ? ` (${spend / cards} a card)` : ''}${credits && !short ? ` · ${credits.balance - spend} left` : ''}`;
  const shapeSummary =
    shape.needs === 'quote' && input.quote?.author.trim()
      ? `${shape.title} · ${input.quote.author.trim()}’s review`
      : shape.needs === 'quote' && input.quote?.text.trim()
        ? `${shape.title} · your pasted review`
        : shape.title;

  return (
    <div
      className={`nx-wiz${over ? ' drop' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={handleDrop}
    >
      {step > 1 &&
        (() => {
          const list = (
            <ol className="nx-rail" aria-label="Steps">
              {rail.map((r) => {
                const state = r.n < step ? 'done' : r.n === step ? 'now' : 'todo';
                return (
                  <li key={r.n} className={state}>
                    {state === 'done' ? (
                      <button
                        type="button"
                        onClick={() => setStep(r.n)}
                        aria-label={`Back to ${r.label}`}
                      >
                        <span className="n">✓</span>
                        <span>{r.label}</span>
                      </button>
                    ) : (
                      <>
                        <span className="n">{r.n}</span>
                        <span>{r.label}</span>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          );
          return railSlot ? createPortal(list, railSlot) : list;
        })()}

      {step === 1 && (
        <Moment
          kind={kind}
          onKind={chooseKind}
          line={line}
          onLine={setLine}
          placeholder={template?.momentHint ?? MOMENT_HINT}
          error={tooLong}
          credits={credits}
          spend={spend}
          cards={chosenPhotos.length ? cards : undefined}
          seconds={seconds}
          onSeconds={setSeconds}
          fidelity={fidelity}
          onFidelity={setFidelity}
          shapeId={shapeId}
          onShape={(id) => {
            const s = SHAPES.find((x) => x.id === id);
            if (s) chooseShape(s);
          }}
          following={following}
          followingLine={
            following
              ? template
                ? followingLine(kind, template, following.handle)
                : `Following @${following.handle}’s ${kind === 'Reel' ? 'reel' : 'post'}`
              : undefined
          }
          onLetGo={letGo}
          onProven={onFindProven ? () => setProven(true) : undefined}
          attachments={
            path === 'ai' && touched
              ? chosenPhotos.map((m) => ({ id: m.id, label: m.label, url: m.url }))
              : []
          }
          onAttach={onUpload}
          onDetach={(id) => setPicks((cur) => cur.filter((x) => x !== id))}
          busy={busy}
          onGo={go}
          quiet={{ label: 'Or start from your own photos or a video, no AI', onClick: startByHand }}
        />
      )}

      {step === 2 && path === 'ai' && (
        <>
          <div className="nx-wiz-head">
            <span className="nx-kick">{kind} · step 2 of 2</span>
            <h2>{useHeading}</h2>
            {failure && (
              <p role="alert" className="nx-fail">
                {failure}
              </p>
            )}
            <p>
              {finding === 'invalid'
                ? 'Your idea is too long for photo matching. Shorten it, or choose photos yourself.'
                : finding === 'failed'
                  ? 'Your photo search was interrupted. Try again or choose photos yourself.'
                  : finding === 'looking'
                    ? 'Finding the ones that show what you wrote.'
                    : n === 0
                      ? 'With no photo of yours in it, the picture is made from your words alone.'
                      : kind === 'Reel'
                        ? 'They show what you described. For a Reel they are the place the clip is filmed in: your railing, your view, your tub, with the moment happening in it.'
                        : 'They show what you described. From them AI makes a new picture of the same place, as you described it: reimagined, not your photo as shot, and marked AI.'}
            </p>
          </div>
          <div className="nx-wiz-body">
            {(finding === 'failed' || finding === 'invalid') && (
              <div role="alert">
                <p>
                  {finding === 'invalid'
                    ? `Photo matching takes up to ${BRIEF_MAX.toLocaleString('en-US')} characters. Your words are saved.`
                    : 'Could not choose photos. Your idea is saved.'}
                </p>
                {finding === 'invalid' ? (
                  <button type="button" className="nx-chip" onClick={editIdea}>
                    Edit your idea
                  </button>
                ) : (
                  <button type="button" className="nx-chip" onClick={() => find(kind, line)}>
                    Try choosing photos again
                  </button>
                )}
                <button
                  type="button"
                  className="nx-chip"
                  onClick={() => {
                    setFinding('done');
                    setChanging(true);
                    setTouched(true);
                  }}
                >
                  Choose photos myself
                </button>
              </div>
            )}
            {finding === 'looking' ? (
              <>
                <div className="nx-use ghost" aria-hidden>
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="pic" style={{ ['--d' as string]: `${i * 140}ms` }} />
                  ))}
                </div>
                <div className="nx-wiz-photos small nx-sweep" aria-hidden>
                  {usablePhotos.map((m) => (
                    <span key={m.id} className="nx-wiz-tile still" style={mediaStyle(m)} />
                  ))}
                </div>
              </>
            ) : (
              <>
                {n > 0 && (
                  <ol className="nx-use" aria-label="The photos we’d use">
                    {chosenPhotos.map((m, i) => (
                      <li key={m.id} style={{ ['--d' as string]: `${i * 140}ms` }}>
                        <span className="pic" style={mediaStyle(m)} />
                        <span className="name">{m.label}</span>
                        <span className="why">{whyOf(m.id)}</span>
                        <button
                          type="button"
                          className="nx-chip"
                          aria-label={`Remove ${m.label}`}
                          onClick={() => tap(m)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
                {changing && (
                  <>
                    <span className="label-caps">Tap a photo to add or remove it</span>
                    <div className="nx-wiz-photos small">
                      {usablePhotos.map((m) => {
                        const at = picks.indexOf(m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={`nx-wiz-tile${at >= 0 ? ' on' : ''}`}
                            aria-pressed={at >= 0}
                            aria-label={m.label}
                            title={m.label}
                            style={mediaStyle(m)}
                            onClick={() => tap(m)}
                          >
                            {at >= 0 && <span className="n">{at + 1}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
            {path === 'ai' && shape.needs === 'quote' && (
              <div className="nx-need">
                <div className="nx-need-head">
                  <h3>Guest quote · which review?</h3>
                  {reviews.length > 0 && !pasting && (
                    <button
                      type="button"
                      className="nx-quiet"
                      onClick={() => {
                        setPasting(true);
                        setReviewId(null);
                        setInput((cur) => ({ ...cur, quote: { text: '', author: '' } }));
                      }}
                    >
                      Paste one instead
                    </button>
                  )}
                  {reviews.length > 0 && pasting && (
                    <button type="button" className="nx-quiet" onClick={() => setPasting(false)}>
                      Pick from your reviews instead
                    </button>
                  )}
                </div>
                {reviews.length > 0 && !pasting ? (
                  <>
                    <div className="nx-reviews" role="radiogroup" aria-label="Which review">
                      {reviews.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          role="radio"
                          aria-checked={reviewId === r.id}
                          className={`nx-review${reviewId === r.id ? ' on' : ''}`}
                          onClick={() => {
                            setReviewId(r.id);
                            setInput((cur) => ({
                              ...cur,
                              quote: { text: r.quote, author: r.author },
                            }));
                          }}
                        >
                          <span className="q">{r.quote}</span>
                          <span className="by">
                            {r.author}
                            {r.reviewedAt ? ` · ${r.reviewedAt}` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="nx-note fine">
                      From the {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'} we
                      imported with your listing. The quote goes on the picture word for word; the
                      caption says who said it.
                    </p>
                  </>
                ) : (
                  <div className="nx-need-fields">
                    <label style={{ gridColumn: '1 / -1' }}>
                      The review
                      <textarea
                        aria-label="The review"
                        rows={3}
                        value={input.quote?.text ?? ''}
                        placeholder="Paste what the guest wrote"
                        onChange={(e) =>
                          setInput((cur) => ({
                            ...cur,
                            quote: { text: e.target.value, author: cur.quote?.author ?? '' },
                          }))
                        }
                      />
                    </label>
                    <label>
                      Who said it
                      <input
                        aria-label="Who said it"
                        value={input.quote?.author ?? ''}
                        placeholder="Megan"
                        onChange={(e) =>
                          setInput((cur) => ({
                            ...cur,
                            quote: { text: cur.quote?.text ?? '', author: e.target.value },
                          }))
                        }
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
            {path === 'ai' && shape.needs === 'dates' && (
              <div className="nx-need">
                <div className="nx-need-head">
                  <h3>Promote dates · what’s open?</h3>
                </div>
                <div className="nx-need-fields three">
                  <label>
                    Open dates
                    <input
                      aria-label="Open dates"
                      value={input.dates?.dates ?? ''}
                      placeholder="Oct 17–19"
                      onChange={(e) =>
                        setInput((cur) => ({
                          ...cur,
                          dates: {
                            ...(cur.dates ?? { price: '', link: '' }),
                            dates: e.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                  <label>
                    Price
                    <input
                      aria-label="Price"
                      value={input.dates?.price ?? ''}
                      placeholder="from $389 a night"
                      onChange={(e) =>
                        setInput((cur) => ({
                          ...cur,
                          dates: {
                            ...(cur.dates ?? { dates: '', link: '' }),
                            price: e.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                  <label>
                    Booking link
                    <input
                      aria-label="Booking link"
                      value={input.dates?.link ?? ''}
                      placeholder="https://"
                      onChange={(e) =>
                        setInput((cur) => ({
                          ...cur,
                          dates: {
                            ...(cur.dates ?? { dates: '', price: '' }),
                            link: e.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                </div>
                <p className="nx-note fine">
                  The dates and the price go on the picture; the caption sends people to the link.
                </p>
              </div>
            )}
          </div>
          <div className="nx-wiz-actions">
            <button
              type="button"
              className="btn btn-primary hv"
              disabled={finding !== 'done' || !goOn || busy || !!blocked}
              title={blocked ?? undefined}
              onClick={make}
            >
              {busy && !adding ? 'Starting…' : makeLabel} <span aria-hidden>▸</span>
            </button>
            <button
              type="button"
              className="nx-chip big"
              disabled={finding !== 'done'}
              onClick={() => setChanging((c) => !c)}
            >
              Change photos
            </button>
            <button type="button" className="nx-chip big" onClick={onUpload} disabled={busy}>
              {adding ? 'Adding…' : 'Add from this device'}
            </button>
            <span className="nx-wiz-sum">
              {kind === 'Post' && finding === 'done' && <span>{pictureMadeBy(mode)} · </span>}
              <span>{short ? creditsLeft : shapeSummary}</span>
            </span>
            {kind === 'Post' && finding === 'done' && n > 0 && (
              <button type="button" className="nx-quiet" disabled={busy} onClick={useAsTheyAre}>
                Use my photos as they are, no AI
              </button>
            )}
          </div>
        </>
      )}

      {step === 2 && path === 'own' && (
        <>
          <div className="nx-wiz-head">
            <span className="nx-kick">
              {kind === 'Reel' ? 'Reel' : 'Post'} · step 2 of 2 · by hand
            </span>
            <h2>{kind === 'Reel' ? 'Your video' : 'Your photos, as they are'}</h2>
            <p>
              {kind === 'Reel'
                ? 'Tap the video to use it. The Reel draft may be cropped to vertical and trimmed to 90 seconds. You write the caption.'
                : 'No AI on this path: what you tap is the post, in the order you tap it, the first one is the cover. You write the caption.'}
            </p>
          </div>
          <div className="nx-wiz-body">
            {kind === 'Reel' ? (
              usableVideos.length ? (
                <div className="nx-wiz-videos">
                  {usableVideos.map((m) => (
                    <VideoTile key={m.id} media={m} on={picks.includes(m.id)} onTap={tap} />
                  ))}
                </div>
              ) : (
                <span className="nx-note" style={{ fontSize: 13 }}>
                  No videos yet. Add one from this device.
                </span>
              )
            ) : usablePhotos.length ? (
              <div className="nx-wiz-photos">
                {usablePhotos.map((m) => {
                  const at = picks.indexOf(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`nx-wiz-tile${at >= 0 ? ' on' : ''}`}
                      aria-pressed={at >= 0}
                      aria-label={m.label}
                      title={m.label}
                      style={mediaStyle(m)}
                      onClick={() => tap(m)}
                    >
                      {at >= 0 && <span className="n">{at + 1}</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span className="nx-note" style={{ fontSize: 13 }}>
                None of the listing photos have a file yet. Add photos from this device.
              </span>
            )}
          </div>
          <div className="nx-wiz-actions">
            <button
              type="button"
              className="btn btn-primary hv"
              disabled={!goOn || busy}
              onClick={make}
            >
              {busy && !adding ? 'Starting…' : makeLabel} <span aria-hidden>▸</span>
            </button>
            <button type="button" className="nx-chip big" onClick={onUpload} disabled={busy}>
              {adding ? 'Adding…' : 'Add from this device'}
            </button>
            <span className="nx-wiz-sum">{pickSummary(kind, chosen, 'own')}</span>
            <button type="button" className="nx-quiet" onClick={backToAi}>
              Let AI make it instead
            </button>
          </div>
        </>
      )}

      {proven && onFindProven && (
        <ProvenPicker
          kind={kind}
          load={onFindProven}
          onPick={follow}
          onClose={() => setProven(false)}
        />
      )}
      {over && <div className="nx-drop-hint">Drop to add</div>}
    </div>
  );
}

function VideoTile({
  media,
  on,
  onTap,
}: {
  media: MediaDto;
  on: boolean;
  onTap: (m: MediaDto) => void;
}) {
  const secs = media.durationSeconds ?? 0;
  const len = `${Math.floor(secs / 60)}:${String(Math.round(secs % 60)).padStart(2, '0')}`;
  return (
    <button
      type="button"
      className={`nx-wiz-tile video${on ? ' on' : ''}`}
      aria-pressed={on}
      aria-label={media.label}
      title={media.label}
      style={mediaStyle(media)}
      onClick={() => onTap(media)}
    >
      <span className="play" aria-hidden>
        ▶
      </span>
      {secs > 0 && <span className="len">{len}</span>}
    </button>
  );
}

/** "Oct 2": the day the monthly credits come back. */
function resetDay(iso: string): string {
  const d = new Date(iso);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]} ${d.getDate()}`;
}
